import { AISearchHeader, Box, newToast, Stack, useElementBreakpoints } from "@compass/core";
import { CustomFilters } from "components";
import { CustomEntityOption } from "components/common/CustomFilters/CustomFilter";
import {
  AiPrompt,
  CustomFilterEntity,
  LkRoleId,
  LkStudentEnrollmentDisplayFieldId,
  LkStudentTabId,
  StudentEnrollmentFilterResource,
  StudentFilter as StudentFilterModel,
  StudentFilterResource,
  StudentListFilter,
  StudentServiceFilterResource,
} from "generated/openApi";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { StudentEnrollmentFilterAccordion } from "../student-enrollments/StudentEnrollmentFilterAccordion";
import { StudentServiceFilterAccordion } from "../student-services/StudentServiceFilterAccordion";
import { StudentDemographicsFilterAccordion } from "./StudentDemographicsFilterAccordion";
import { FilterAccordion } from "components/common/CustomFilters/FilterAccordion";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { setAppToast } from "redux/reducers/appSlice";

export const StudentFilter = (props: {
  filter: StudentListFilter;
  setFilter: (filter: StudentListFilter) => void;
  aiSearchPrompt?: string;
  setAiSearchPrompt?: (value: string) => void;
}) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<StudentFilterResource>({} as StudentFilterResource);
  const [enrollmentResource, setEnrollmentResource] = useState<StudentEnrollmentFilterResource>({});
  const [serviceResource, setServiceResource] = useState<StudentServiceFilterResource>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>();
  const [localAiSearchPrompt, setLocalAiSearchPrompt] = useState("");
  const [completedAiSearchPrompt, setCompletedAiSearchPrompt] = useState("");
  const aiSearchPrompt = props.aiSearchPrompt ?? localAiSearchPrompt;
  const setAiSearchPrompt = props.setAiSearchPrompt ?? setLocalAiSearchPrompt;
  const aiSearchContext = "StudentFilters";
  const properties = resource.properties || [];
  const dispatch = useDispatch();
  const displayFields = resource.enrollmentDisplayFields || [];
  const studentTabIds = resource.studentTabs?.map((x) => x.id);

    // Gets the current logged in user's role
    const appUserRoleId: number | undefined = useSelector((state: RootState) => state.app.roleId);
    const appUserId: number | undefined = useSelector((state: RootState) => state.app.userId);

  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);

  const entityOptions: CustomEntityOption[] = [
    { EntityType: CustomFilterEntity.Student, EntityName: "Demographics" },
    { EntityType: CustomFilterEntity.StudentEnrollment, EntityName: "Enrollments" },
    { EntityType: CustomFilterEntity.StudentForm, EntityName: "Forms" },
    // { EntityType: CustomFilterEntity.StudentApplication, EntityName: "Application" },
    // { EntityType: CustomFilterEntity.StudentDiploma, EntityName: "Diploma" },
    // { EntityType: CustomFilterEntity.StudentFafsa, EntityName: "FAFSA" },
    // { EntityType: CustomFilterEntity.StudentGpa, EntityName: "GPA" },
    // { EntityType: CustomFilterEntity.StudentGrades, EntityName: "Grades" },
    // { EntityType: CustomFilterEntity.StudentParent, EntityName: "Parent" },
    // { EntityType: CustomFilterEntity.StudentPostHighSchool, EntityName: "Post HS Plans" },
    // { EntityType: CustomFilterEntity.PostSecondary, EntityName: "Post Secondary" },
    // { EntityType: CustomFilterEntity.StudentService, EntityName: "Services" },
    // { EntityType: CustomFilterEntity.StudentScholarship, EntityName: "Scholarship" },
    // { EntityType: CustomFilterEntity.StudentTest, EntityName: "Test Scores" },
  ];

  if (studentTabIds?.includes(LkStudentTabId.Absence)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentAbsence, EntityName: "Absences" });
  }
  if (studentTabIds?.includes(LkStudentTabId.ApplicationResponse)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentApplication, EntityName: "Application" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Diploma)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentDiploma, EntityName: "Diploma" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Fafsa)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentFafsa, EntityName: "FAFSA" });
  }
  //     if (studentTabIds?.includes(LkStudentTabId.Questionnaire)){
  //   entityOptions.push({ EntityType: CustomFilterEntity.StudentForm, EntityName: "Forms" });
  // }
  if (studentTabIds?.includes(LkStudentTabId.Gpa)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentGpa, EntityName: "GPA" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Grade)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentGrades, EntityName: "Grades" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Parent)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentParent, EntityName: "Parent" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Nsc)) {
    entityOptions.push({ EntityType: CustomFilterEntity.PostSecondary, EntityName: "Post Secondary" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Service)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentService, EntityName: "Services" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Scholarship)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentScholarship, EntityName: "Scholarship" });
  }
  if (studentTabIds?.includes(LkStudentTabId.Test)) {
    entityOptions.push({ EntityType: CustomFilterEntity.StudentTest, EntityName: "Test Scores" });
  }



  useEffect(() => {
    ApiService.students.getFilterResource().then((response) => {
      setResource(response);
    });

    ApiService.studentEnrollments.getFilterResource().then((response) => {
      setEnrollmentResource(response);
    });

  }, []);

  const submitAiFilter = (prompt: string) => {
    const request: AiPrompt = { prompt, customFilterEntities: entityOptions.map((e) => e.EntityType) }
    return ApiService.openAi.promptStudentFilters(request).then((response) => {
      props.setFilter(response);
      setValidationErrors({});
      setCompletedAiSearchPrompt(prompt);
    }).catch((ex) => {
      dispatch(setAppToast(newToast("error")));
      setValidationErrors(ex.data);
    });;
  }

  const handleFavoriteToast = useCallback((message: string) => {
    const type = message.includes("unavailable") ? "error" : "success";
    dispatch(setAppToast(newToast(type, message)));
  }, [dispatch]);

  const loadFavoriteSearches = useCallback(() => {
    return ApiService.aiSearchFavorites.getList(aiSearchContext);
  }, []);

  const saveFavoriteSearch = useCallback((favorite) => {
    return ApiService.aiSearchFavorites.saveCreate({
      id: typeof favorite.id === "number" ? favorite.id : undefined,
      name: favorite.name,
      query: favorite.query,
      searchContext: aiSearchContext,
      isFavorite: favorite.isFavorite,
      displayOrder: favorite.displayOrder ?? favorite.order,
    });
  }, []);

  const updateFavoriteSearch = useCallback((favorite) => {
    return ApiService.aiSearchFavorites.saveUpdate({
      id: typeof favorite.id === "number" ? favorite.id : undefined,
      name: favorite.name,
      query: favorite.query,
      searchContext: aiSearchContext,
      isFavorite: favorite.isFavorite,
      displayOrder: favorite.displayOrder ?? favorite.order,
    });
  }, []);

  const deleteFavoriteSearch = useCallback((id) => {
    return ApiService.aiSearchFavorites.delete(Number(id));
  }, []);

  const markFavoriteSearchUsed = useCallback((id) => {
    return ApiService.aiSearchFavorites.markUsed(Number(id));
  }, []);

  const saveFavoriteSearchOrder = useCallback((favorites) => {
    return ApiService.aiSearchFavorites.saveOrder(
      favorites
        .filter((favorite) => typeof favorite.id === "number")
        .map((favorite) => ({ id: favorite.id as number, displayOrder: favorite.displayOrder }))
    );
  }, []);

  return (
    <Stack ref={breakpointsRef} spacing="md" padding={2}>
      <CustomFilters
        allowedEntityTypes={entityOptions}
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />
      <Box>
        {((appUserRoleId == LkRoleId.Cobro || appUserRoleId == LkRoleId.Administrator) && resource.canAccessAiFeature) && (
          <AISearchHeader
            value={aiSearchPrompt}
            onChange={setAiSearchPrompt}
            onSubmit={submitAiFilter}
            validationErrors={validationErrors}
            showFavorites
            userId={appUserId}
            completedSearch={completedAiSearchPrompt}
            onFavoriteToast={handleFavoriteToast}
            loadFavoriteSearches={loadFavoriteSearches}
            createFavoriteSearch={saveFavoriteSearch}
            updateFavoriteSearch={updateFavoriteSearch}
            deleteFavoriteSearch={deleteFavoriteSearch}
            markFavoriteSearchUsed={markFavoriteSearchUsed}
            saveFavoriteSearchOrder={saveFavoriteSearchOrder}
          />
        )}
        {/* <FilterAccordion title="" defaultExpanded> */}
        <StudentDemographicsFilterAccordion
          filter={props.filter.studentFilter!}
          resource={resource}
          setFilter={(subFilter) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.studentFilter = subFilter;
              })
            )
          }
        />
        {/* </FilterAccordion> */}
      </Box>
      {/* 
      <Box>
        <FilterAccordion title="Enrollment">
          <StudentEnrollmentFilterAccordion
            filter={props.filter}
            setFilter={props.setFilter}
            resource={enrollmentResource}
          /></FilterAccordion>
      </Box> */}
    </Stack>
  );
};
